import { AwaitContainer } from '@ibsys/kit';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useExpertReportEvaluation } from '@/entities/expert-report';
import type { EvaluationBlock, EvaluationRuleResult } from '@/entities/expert-report';
import { useHasPermission } from '@/entities/profile';
import { useSettings } from '@/entities/settings';
import {
  ExpertReportModelModeContext,
  ExpertReportResultEditContext,
  useFullyShownBlocks,
  type EvaluationRowResult,
} from '../model';
import { ExpertReportEvaluationTable } from './ExpertReportEvaluationTable';

type Props = {
  conversationId: number;
  expertReportId: number;
};

export type FlatRow = EvaluationRowResult & {
  id: string;
  blockId: number;
  name: string;
  rules?: EvaluationRuleResult[];
  depth: number;
  isParent: boolean;
  isTotal: boolean;
  /** У наименования блока есть кнопка полного показа — только у верхнеуровневых блоков */
  canShowFull: boolean;
  /** Блок показан целиком вопреки отбору */
  isShownFull: boolean;
  /** Блок последней строки поддерева — ею заканчивается показанный целиком блок */
  lastSubtreeBlockId: number;
};

/** Отбор не выставлен — показаны правила всех результатов. */
const NO_PICKED_RESULTS: string[] = [];

/** Оставляет правила с отобранными результатами; пустой отбор оставляет правила всех результатов. */
const pickRulesByResults = (rules: EvaluationRuleResult[] | undefined, pickedResults: string[]) =>
  pickedResults.length > 0 ? (rules?.filter((rule) => pickedResults.includes(rule.result)) ?? []) : rules;

/** Блок, развёрнутый в строки, вместе с тем, что о нём сказал отбор. */
type FlattenedBlock = {
  /** Строки блока и его показанных потомков */
  rows: FlatRow[];
  /** Отбор не оставил в блоке ни одного правила — вложенный блок с таким признаком не показывается */
  isHiddenByFilter: boolean;
  /** Отбор скрыл в блоке правило или вложенный блок */
  hasHiddenContent: boolean;
};

/**
 * Разворачивает блок и его потомков в плоские строки, оставляя правила с отобранными результатами
 * (пустой отбор — показаны правила всех результатов).
 *
 * Блок из `shownFullBlockIds` показывается целиком вместе со всем своим поддеревом: внутри него
 * отбор не действует. Полный показ предлагается только верхнеуровневым блокам — раскрытый блок и
 * так показывает всё поддерево, поэтому своя кнопка у каждого вложенного ничего бы не добавила.
 *
 * Показывать ли блок, решает вышестоящий: собственную строку блок отдаёт всегда, а верхнеуровневые
 * остаются при любом отборе.
 */
const flattenBlock = (
  block: EvaluationBlock,
  depth: number,
  pickedResults: string[],
  shownFullBlockIds: ReadonlySet<number>,
  isInsideShownFull: boolean,
): FlattenedBlock => {
  const isFiltered = pickedResults.length > 0;
  const isTopLevel = depth === 0;
  const isShownFull = isFiltered && isTopLevel && shownFullBlockIds.has(block.blockId);
  const isShownWhole = isInsideShownFull || isShownFull;
  const isParent = block.children.length > 0;
  const children = block.children.map((child) =>
    flattenBlock(child, depth + 1, pickedResults, shownFullBlockIds, isShownWhole),
  );
  const pickedRules = isParent ? undefined : pickRulesByResults(block.rules, pickedResults);
  const rules = isShownWhole && !isParent ? block.rules : pickedRules;
  const hasShownChildren = children.some((child) => !child.isHiddenByFilter);
  const isHiddenByFilter = isFiltered && depth > 0 && !hasShownChildren && !pickedRules?.length;
  const hasHiddenRules = !isParent && (block.rules?.length ?? 0) > (pickedRules?.length ?? 0);
  const hasHiddenChildren = children.some((child) => child.isHiddenByFilter || child.hasHiddenContent);
  const hasHiddenContent = isFiltered && (hasHiddenRules || hasHiddenChildren);
  const childRows = children.filter((child) => isShownWhole || !child.isHiddenByFilter).flatMap((child) => child.rows);
  const row: FlatRow = {
    id: `block-${block.blockId}`,
    blockId: block.blockId,
    name: block.blockName,
    rating: block.rating ?? null,
    maxRating: block.maxRating ?? null,
    manualRating: block.manualRating ?? null,
    value: block.value ?? null,
    values: block.values ?? null,
    manualValue: block.manualValue ?? null,
    possibleValues: block.possibleValues ?? null,
    unit: block.unit ?? null,
    rules,
    depth,
    isParent,
    isTotal: false,
    // Кнопка держится и у раскрытого блока, скрывать в котором уже нечего: ею он и возвращается
    // к отобранному виду
    canShowFull: isTopLevel && (isShownFull || hasHiddenContent),
    isShownFull,
    lastSubtreeBlockId: childRows.at(-1)?.blockId ?? block.blockId,
  };
  return { rows: [row, ...childRows], isHiddenByFilter, hasHiddenContent };
};

/** Разворачивает дерево блоков в плоские строки. */
const flattenBlocks = (
  blocks: EvaluationBlock[],
  depth: number,
  pickedResults: string[],
  shownFullBlockIds: ReadonlySet<number>,
): FlatRow[] => blocks.flatMap((block) => flattenBlock(block, depth, pickedResults, shownFullBlockIds, false).rows);

export const ExpertReportTab: FC<Props> = ({ conversationId, expertReportId }) => {
  const { t } = useTranslation(['dialogs']);
  const { dialogsPage } = useSettings();
  // Отбор пуст — пользователь его не выставлял, показано всё заключение целиком
  const pickedResults = dialogsPage?.expertReportRuleResults ?? NO_PICKED_RESULTS;
  const isFiltered = pickedResults.length > 0;
  const fullyShown = useFullyShownBlocks(`${conversationId}-${expertReportId}-${pickedResults.join(',')}`);
  const { data, isLoading } = useExpertReportEvaluation(conversationId, expertReportId);
  // Правка результата открыта отдельным разрешением: право читает контейнер и раздаёт его вниз
  // одним признаком — спрашивать его в каждой строке заключения незачем
  const canEditResult = useHasPermission('EDIT_EXPERT_REPORT_RESULT');
  const rows = useMemo<FlatRow[]>(() => {
    if (!data?.evaluation) return [];
    const { evaluation } = data;
    // Итог показывается в том виде, в каком его дало правило расчёта: значение справочника —
    // значением, набор — перечислением, балл — баллом с максимумом, а итог, в который не вошёл
    // ни один балл, — прочерком
    const totalRow: FlatRow = {
      id: 'total',
      blockId: 0,
      name: t('expertReport.total'),
      rating: evaluation.totalRating ?? null,
      maxRating: evaluation.totalMaxRating ?? null,
      manualRating: null,
      value: evaluation.totalValue ?? null,
      values: evaluation.totalValues ?? null,
      manualValue: null,
      possibleValues: evaluation.totalPossibleValues ?? null,
      unit: evaluation.totalUnit ?? null,
      depth: 0,
      isParent: false,
      isTotal: true,
      canShowFull: false,
      isShownFull: false,
      lastSubtreeBlockId: 0,
    };
    // Баллы берутся из оценки как есть: отбор меняет состав показанного, а не саму оценку
    return [totalRow, ...flattenBlocks(evaluation.blocks, 0, pickedResults, fullyShown.blockIds)];
  }, [data, pickedResults, fullyShown.blockIds, t]);

  if (isLoading) return <AwaitContainer isLoading />;
  return (
    // Режим модели заключения нужен ссылкам на знание в комментариях результата — они лежат
    // глубоко в заключении, поэтому режим отдаётся контекстом, а не по цепочке пропов.
    // Результата нет вовсе — режим неважен: показывать в заключении нечего
    <ExpertReportModelModeContext.Provider value={data?.expertReport.modelMode ?? 'legacy'}>
      <ExpertReportResultEditContext.Provider value={canEditResult}>
        <ExpertReportEvaluationTable
          rows={rows}
          expertReportId={expertReportId}
          conversationId={conversationId}
          isFiltered={isFiltered}
          verificationStatus={data?.verificationStatus}
          shownBlockId={fullyShown.shownBlockId}
          onToggleFullBlock={fullyShown.toggleBlock}
        />
      </ExpertReportResultEditContext.Provider>
    </ExpertReportModelModeContext.Provider>
  );
};
